/**
 * Game Constants
 */

// Astronomical Units
export const AU = 149597870.7; // km per AU
export const AU_TO_RENDERING_UNITS = 100; // Scale factor for rendering (1 AU = 100 units in scene)

// Time
export const SECONDS_PER_DAY = 86400;
export const DAYS_PER_YEAR = 365.25;

// Starting values
export const STARTING_CAPITAL = 50_000_000; // $50M
export const STARTING_REPUTATION = 0;

// Player backgrounds
export enum PlayerBackground {
  ENGINEER = 'engineer',
  ASTRONOMER = 'astronomer',
  ECONOMIST = 'economist',
  BUSINESS_DEVELOPER = 'business_developer',
}

// Background bonuses
export const BACKGROUND_BONUSES = {
  [PlayerBackground.ENGINEER]: {
    techTreeSpeedBonus: 0.25, // 25% faster tech unlocks
    anomalyReduction: 0.20, // 20% less anomalies/failures
  },
  [PlayerBackground.ASTRONOMER]: {
    travelTimeBonus: 0.10, // 10% faster travel
    bonusMaterialChance: 0.15, // 15% more chance of bonus materials
  },
  [PlayerBackground.ECONOMIST]: {
    priceVarianceBonus: 0.30, // 30% higher price variance (more opportunities)
    tradingBonus: 0.15, // 15% better trade margins
  },
  [PlayerBackground.BUSINESS_DEVELOPER]: {
    equipmentRentalDiscount: 0.20, // 20% discount on rentals
    storageDiscount: 0.20, // 20% discount on storage
  },
};

// Celestial body colors (realistic approximations)
export const BODY_COLORS = {
  SUN: 0xfdb813,
  MERCURY: 0x9d9d9d,
  VENUS: 0xe8cda2,
  EARTH: 0x2f6a9e,
  MARS: 0xc1440e,
  JUPITER: 0xd8ca9d,
  SATURN: 0xe3d8b8,
  URANUS: 0x4fd0e0,
  NEPTUNE: 0x4166f5,
  MOON: 0xaaaaaa,
  ISS: 0xcccccc,
  ASTEROID_C: 0xcccccc, // C-type: light grey (was too dark)
  ASTEROID_S: 0xcccccc, // S-type: light grey (was too dark)
  ASTEROID_M: 0xcccccc, // M-type: light grey (was too dark)
};

// Simplified orbital data (semi-major axis in AU, for initial positioning)
// Planet orbital distances in AU (semi-major axis)
export const PLANET_ORBITS: Record<string, number> = {
  Mercury: 0.387,
  Venus: 0.723,
  Earth: 1.0,
  Mars: 1.524,
  Jupiter: 5.204,
  Saturn: 9.537,
  Uranus: 19.191,
  Neptune: 30.069,
};

// Planet orbital periods in days
export const PLANET_ORBITAL_PERIODS: Record<string, number> = {
  Mercury: 87.97,
  Venus: 224.7,
  Earth: 365.25,
  Mars: 686.98,
  Jupiter: 4332.59,
  Saturn: 10759.22,
  Uranus: 30688.5,
  Neptune: 60182,
};

// Planet diameters in km
export const PLANET_DIAMETERS: Record<string, number> = {
  Mercury: 4879,
  Venus: 12104,
  Earth: 12742,
  Mars: 6779,
  Jupiter: 139820,
  Saturn: 116460,
  Uranus: 50724,
  Neptune: 49244,
  Sun: 1392700,
};

// Moon diameters in km
export const MOON_DIAMETERS: Record<string, number> = {
  Moon: 3474,
  Io: 3643,
  Europa: 3122,
  Ganymede: 5268,
  Callisto: 4821,
};

// Moon data (orbital distance in km from parent planet)
export interface MoonData {
  name: string;
  parent: string; // Planet name
  orbitKm: number; // Distance from parent in km
  size: number; // Render size
  color: number;
  orbitalPeriodDays?: number; // For realistic orbital speeds
}

export const MOONS: MoonData[] = [
  // Earth's Moon
  {
    name: 'Moon',
    parent: 'Earth',
    orbitKm: 384400,
    size: 0.8,
    color: BODY_COLORS.MOON,
    orbitalPeriodDays: 27.3,
  },
  // Jupiter's Galilean moons
  {
    name: 'Io',
    parent: 'Jupiter',
    orbitKm: 421700,
    size: 0.7,
    color: 0xffcc66, // Yellowish (sulfur)
    orbitalPeriodDays: 1.77,
  },
  {
    name: 'Europa',
    parent: 'Jupiter',
    orbitKm: 671100,
    size: 0.6,
    color: 0xccddee, // Icy white-blue
    orbitalPeriodDays: 3.55,
  },
  {
    name: 'Ganymede',
    parent: 'Jupiter',
    orbitKm: 1070400,
    size: 0.9,
    color: 0xbbaa99, // Grey-brown
    orbitalPeriodDays: 7.15,
  },
  {
    name: 'Callisto',
    parent: 'Jupiter',
    orbitKm: 1882700,
    size: 0.8,
    color: 0x998877, // Dark grey-brown
    orbitalPeriodDays: 16.69,
  },
];

// ISS data (special case - human-made satellite)
export const ISS_DATA = {
  name: 'ISS',
  parent: 'Earth',
  orbitKm: 6371 + 408, // Earth radius + altitude
  size: 0.15, // Very small
  color: BODY_COLORS.ISS,
  orbitalPeriodDays: 0.063, // ~90 minutes = ~0.063 days
};

// Camera settings
export const CAMERA_FOV = 75;
export const CAMERA_NEAR = 0.1;
export const CAMERA_FAR = 10000;
export const CAMERA_DEFAULT_DISTANCE = 300;
export const CAMERA_MIN_DISTANCE = 50;
export const CAMERA_MAX_DISTANCE = 2000;
export const CAMERA_ROTATION_SPEED = 0.005;
export const CAMERA_PAN_SPEED = 0.5;
export const CAMERA_ZOOM_SPEED = 1.1;
export const CAMERA_FOCUS_DISTANCE_MULTIPLIER = 8;

// Rendering settings
export const STAR_FIELD_RADIUS = 4000;
export const STAR_COUNT = 2000;
export const SUN_RADIUS = 10;
export const SUN_GLOW_RADIUS = 15;
export const ORBIT_LINE_SEGMENTS = 128;
export const CIRCLE_SEGMENTS = 64;
export const SPHERE_DETAIL = 64;
export const DRAG_THRESHOLD_PX = 5;

// Asteroid visibility
export const TARGET_VISIBLE_ASTEROIDS = 50;
export const VISIBILITY_UPDATE_INTERVAL_MS = 500;

// UI timing
export const TICKER_SPEED = 80;
export const MAX_TICKER_ITEMS = 10;
export const COPY_BUTTON_RESET_MS = 2000;
export const FEEDBACK_DELAY_MS = 2000;
export const DEFAULT_CONTRACT_COUNT = 4;

// Resource types
export enum ResourceType {
  WATER = 'water',
  LITHIUM = 'lithium',
  RARE_EARTH = 'rare_earth',
  PLATINUM = 'platinum',
  GOLD = 'gold',
  IRON = 'iron',
  NICKEL = 'nickel',
  VOLATILES = 'volatiles',
}

// Base resource prices ($/ton)
export const RESOURCE_BASE_PRICES = {
  [ResourceType.WATER]: 5000,
  [ResourceType.LITHIUM]: 45000,
  [ResourceType.RARE_EARTH]: 80000,
  [ResourceType.PLATINUM]: 150000,
  [ResourceType.GOLD]: 200000,
  [ResourceType.IRON]: 1000,
  [ResourceType.NICKEL]: 2000,
  [ResourceType.VOLATILES]: 8000,
};

// Unlock thresholds
export const ULA_UNLOCK_THRESHOLD = 100_000_000; // $100M

/**
 * UI Colors - Match CSS variables from index.html
 * These are hex values for use in JS/Canvas; use CSS variables in HTML where possible.
 */
export const UI_COLORS = {
  // Primary HUD colors
  HUD_CYAN: '#54e6ff',
  HUD_CYAN_2: '#1aa9ff',
  HUD_BLUE: '#4da6ff',
  
  // Legacy aliases
  PRIMARY: '#0f0',
  SECONDARY: '#54e6ff',  // Now matches --hud-cyan
  WARNING: '#ff0',
  DANGER: '#f00',
  
  // Text colors
  TEXT: 'rgba(235, 245, 255, 0.92)',
  TEXT_MUTED: 'rgba(180, 200, 220, 0.7)',
  MUTED: '#888',  // For canvas contexts
};

// Time scale levels
export const TIME_SCALES = {
  REAL_TIME: 0.001,
  ONE_HOUR_PER_SEC: 0.042,       // 1 hour/sec (1/24 day)
  FOUR_HOURS_PER_SEC: 0.167,     // 4 hours/sec (1/6 day)
  EIGHT_HOURS_PER_SEC: 0.333,    // 8 hours/sec (1/3 day)
  ONE_DAY_PER_SEC: 1.0,          // 1 day/sec
  ONE_WEEK_PER_SEC: 7.0,         // 1 week/sec (7 days)
  TWO_WEEKS_PER_SEC: 14.0,       // 2 weeks/sec (14 days)
  ONE_MONTH_PER_SEC: 30.0,       // 1 month/sec (~30 days)
};

export const TIME_SCALE_NAMES = [
  'Real-Time',
  '1 Hour/Sec',
  '4 Hours/Sec',
  '8 Hours/Sec',
  '1 Day/Sec',
  '1 Week/Sec',
  '2 Weeks/Sec',
  '1 Month/Sec'
];

export const TIME_SCALE_VALUES = [
  TIME_SCALES.REAL_TIME,
  TIME_SCALES.ONE_HOUR_PER_SEC,
  TIME_SCALES.FOUR_HOURS_PER_SEC,
  TIME_SCALES.EIGHT_HOURS_PER_SEC,
  TIME_SCALES.ONE_DAY_PER_SEC,
  TIME_SCALES.ONE_WEEK_PER_SEC,
  TIME_SCALES.TWO_WEEKS_PER_SEC,
  TIME_SCALES.ONE_MONTH_PER_SEC,
];
